import React, { useEffect, useRef, useState } from 'react';
import { FileText, Mic } from 'lucide-react';
import type { ConfirmDecision, DeckAnswer, DeckCardModel, DeckLabels, DeckOption, DeckWork } from './types';
import { DeckRead } from './DeckRead';

/*
 * One card. The same anatomy on every surface: number and deck size, a tag,
 * the question, one line of why, the input for its kind, then the four ways
 * out: answer, say it, drop a document, skip. The stack around it lives in
 * OrbitDeck; this file only knows about one card.
 */

const WAVE_BARS = Array.from({ length: 16 });

const initials = (text: string): string => text.split(/[\s,]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const PickChips: React.FC<{
  options: { value: string; label: string; glyph?: string }[];
  multi: boolean;
  logo?: boolean;
  picked: string[];
  onPick: (value: string) => void;
}> = ({ options, multi, logo = false, picked, onPick }) => (
  <div className="odeck-chips">
    {options.map((o) => {
      let cls = 'odeck-chip';
      if (logo) cls += ' logo';
      if (picked.includes(o.value)) cls += ' picked';
      return (
        <button key={o.value} type="button" className={cls} onClick={() => onPick(o.value)} aria-pressed={picked.includes(o.value)}>
          {(logo || o.glyph) && <span className="glyph">{o.glyph || initials(o.label)}</span>}
          {o.label}
        </button>
      );
    })}
    {!multi && null}
  </div>
);

interface VoiceState { state: 'idle' | 'rec' | 'done' | 'busy'; seconds: number; text: string; }

export interface DeckCardProps {
  card: DeckCardModel;
  index: number;
  total: number;
  busy?: boolean;
  labels?: DeckLabels;
  /** Why the last answer did not go through. Keeps the card on screen. */
  error?: string;
  /** What Orbit is doing right now. Takes the card over while it runs. */
  work?: DeckWork | null;
  /** The website read has shown its last pass. The caller may move on. */
  onReadDone?: () => void;
  onAnswer: (answer: DeckAnswer) => void;
  onSkip: () => void;
  onDocument: (file: File) => void;
  /** Turns a recording into text. When absent the voice exit is hidden. */
  transcribe?: (blob: Blob) => Promise<string>;
  /** Finds tools by a few typed letters, for the Other option on the tools card. */
  searchTools?: (query: string) => Promise<DeckOption[]>;
}

const detectedValues = (card: DeckCardModel): string[] => (card.options || []).filter((o) => o.detected).map((o) => o.value);

export const DeckCard: React.FC<DeckCardProps> = ({ card, index, total, busy = false, labels = {}, error = '', work = null, onReadDone, onAnswer, onSkip, onDocument, transcribe, searchTools }) => {
  const [picked, setPicked] = useState<string[]>(() => detectedValues(card));
  const [extra, setExtra] = useState<DeckOption[]>([]);
  const [otherText, setOtherText] = useState('');
  const [found, setFound] = useState<DeckOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [text, setText] = useState(card.prefill || '');
  const [decisions, setDecisions] = useState<Record<string, ConfirmDecision>>({});
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [offRows, setOffRows] = useState<string[]>([]);
  const [voice, setVoice] = useState<VoiceState>({ state: 'idle', seconds: 0, text: '' });
  const [upload, setUpload] = useState<{ name: string; pct: number; done: boolean } | null>(null);
  const [over, setOver] = useState(false);
  const [identity, setIdentity] = useState({ name: '', email: '', password: '' });
  const [pw, setPw] = useState({ first: '', again: '' });
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<number | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current); }, []);

  const primary = labels.primary || 'Next';
  let primaryNow = primary;
  if (busy) primaryNow = 'Saving…';
  const busyLabel = labels.busy || 'Saving your answer. Orbit is working out what comes next.';
  const skipLabel = labels.skip || 'Skip, Orbit will find out';
  const num = `CARD ${String(index + 1).padStart(2, '0')} · ${String(total).padStart(2, '0')}`;

  const pickOne = (value: string) => {
    setPicked([value]);
    window.setTimeout(() => onAnswer({ how: 'picked', value }), 260);
  };
  const pickMany = (value: string) => {
    setPicked((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      return [...prev, value];
    });
  };
  const chosen = [...picked.filter((v) => v !== 'other'), ...extra.map((o) => o.value)];
  const doneChoosing = () => {
    if (chosen.length === 0) return;
    onAnswer({ how: 'multi', value: chosen });
  };
  const addExtra = (option: DeckOption) => {
    const value = option.value.trim();
    if (!value) return;
    setExtra((prev) => {
      if (prev.some((o) => o.value === value) || picked.includes(value)) return prev;
      return [...prev, { ...option, value }];
    });
    setOtherText('');
    setFound([]);
  };
  const otherOpen = picked.includes('other');

  useEffect(() => {
    if (!otherOpen || !searchTools || card.search !== 'tools') return undefined;
    const q = otherText.trim();
    if (q.length < 2) { setFound([]); return undefined; }
    let live = true;
    setSearching(true);
    const t = window.setTimeout(async () => {
      try {
        const results = await searchTools(q);
        if (live) setFound(results.slice(0, 6));
      } catch {
        if (live) setFound([]);
      } finally {
        if (live) setSearching(false);
      }
    }, 320);
    return () => { live = false; window.clearTimeout(t); };
  }, [otherText, otherOpen, searchTools, card.search]);
  const submitText = () => {
    const value = text.trim();
    if (!value) return;
    onAnswer({ how: 'typed', value });
  };

  /* ── voice ─────────────────────────────────────────────────────────── */
  const startRecording = async () => {
    if (!transcribe || !navigator.mediaDevices) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setVoice((v) => ({ ...v, state: 'busy' }));
        const blob = new Blob(chunks.current, { type: rec.mimeType || 'audio/webm' });
        try {
          const transcript = await transcribe(blob);
          setVoice((v) => ({ ...v, state: 'done', text: transcript }));
        } catch {
          setVoice({ state: 'idle', seconds: 0, text: '' });
        }
      };
      recorder.current = rec;
      rec.start();
      setVoice({ state: 'rec', seconds: 0, text: '' });
      timer.current = window.setInterval(() => {
        setVoice((v) => {
          if (v.seconds + 1 >= 60) stopRecording();
          return { ...v, seconds: v.seconds + 1 };
        });
      }, 1000);
    } catch {
      setVoice({ state: 'idle', seconds: 0, text: '' });
    }
  };
  const stopRecording = () => {
    if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
  };
  const toggleVoice = () => {
    if (voice.state === 'idle') { startRecording(); return; }
    if (voice.state === 'rec') stopRecording();
  };
  const useVoice = () => {
    if (!voice.text.trim()) return;
    onAnswer({ how: 'voice', value: voice.text.trim() });
  };

  const voiceUi = transcribe && (
    <div className={`odeck-voice ${voice.state === 'rec' ? 'rec' : ''}`}>
      {voice.state !== 'done' && (
        <>
          <button type="button" className="odeck-mic" onClick={toggleVoice} aria-label="Record a voice note" disabled={voice.state === 'busy'}><Mic size={15} /></button>
          <div className="odeck-wave">{WAVE_BARS.map((_, i) => <i key={i} />)}</div>
          <span className="odeck-vhint">{voice.state === 'rec' ? 'listening, tap to finish' : voice.state === 'busy' ? 'transcribing' : 'or say it, 20 seconds is plenty'}</span>
          <span className="odeck-vtimer">{`0:${String(voice.seconds).padStart(2, '0')}`}</span>
        </>
      )}
      {voice.state === 'done' && (
        <>
          <div className="odeck-vquote">“{voice.text}”<span className="odeck-vmeta">TRANSCRIBED · you confirm before it counts</span></div>
          <div className="odeck-vactions">
            <button type="button" className="odeck-chip picked" onClick={useVoice} disabled={busy}>Use it</button>
            <button type="button" className="odeck-chip" onClick={() => setVoice({ state: 'idle', seconds: 0, text: '' })} disabled={busy}>Redo</button>
          </div>
        </>
      )}
    </div>
  );

  /* ── documents ──────────────────────────────────────────────────────── */
  const takeFile = (file: File | undefined) => {
    if (!file) return;
    setUpload({ name: file.name, pct: 8, done: false });
    const tick = window.setInterval(() => {
      setUpload((u) => {
        if (!u || u.done) { window.clearInterval(tick); return u; }
        if (u.pct >= 90) return u;
        return { ...u, pct: u.pct + 14 };
      });
    }, 160);
    onDocument(file);
  };
  const dropZone = (
    <>
      {!upload && (
        <div
          className={`odeck-drop ${over ? 'over' : ''}`}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); takeFile(e.dataTransfer.files?.[0]); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') fileInput.current?.click(); }}
        >
          <div className="odeck-ft-row"><span className="odeck-ft">PDF</span><span className="odeck-ft">DOC</span><span className="odeck-ft">CSV</span><span className="odeck-ft">PNG</span></div>
          <strong>Drop anything Orbit should read</strong>
          <em>Org chart, SOPs, strategy, a tools export. Or click to pick one.</em>
        </div>
      )}
      {upload && (
        <div className="odeck-up">
          <div className="odeck-up-row">
            <b className="odeck-ficon">{(upload.name.split('.').pop() || 'DOC').toUpperCase().slice(0, 4)}</b>
            <div className="odeck-up-meta"><strong>{upload.name}</strong><div className="odeck-up-bar"><b style={{ width: `${upload.pct}%` }} /></div></div>
            <span className="odeck-vtimer">{upload.pct >= 90 ? 'reading' : `${upload.pct}%`}</span>
          </div>
        </div>
      )}
      <input ref={fileInput} type="file" hidden accept=".pdf,.doc,.docx,.txt,.md,.csv,.png,.jpg,.jpeg" onChange={(e) => takeFile(e.target.files?.[0])} />
    </>
  );

  /* ── confirm ───────────────────────────────────────────────────────── */
  const confirmAll = () => {
    const rows = (card.facts || []).map((f): ConfirmDecision => {
      const d = decisions[f.field_key];
      if (d) return d;
      return { field_key: f.field_key, scope: f.scope || '', action: 'confirm' };
    });
    onAnswer({ how: 'confirmed', value: 'confirmed', confirm: rows });
  };
  const setRow = (key: string, scope: string, action: ConfirmDecision['action']) => {
    setDecisions((prev) => ({ ...prev, [key]: { field_key: key, scope, action, value: corrections[key] } }));
  };

  const orgConfirm = () => {
    const all = card.proposals || [];
    const accept = all.filter((p) => !offRows.includes(p.id)).map((p) => p.id);
    const dismiss = all.filter((p) => offRows.includes(p.id)).map((p) => p.id);
    onAnswer({ how: 'confirmed', value: 'confirmed', org: { accept, dismiss } });
  };

  /* ── the body per kind ─────────────────────────────────────────────── */
  const body = (() => {
    if (card.body) return card.body({ answer: onAnswer, skip: onSkip, busy });
    if (card.input === 'confirm') {
      const conf = Math.round((card.confidence || 0.88) * 100);
      return (
        <>
          <div className="odeck-evi">
            <span className="src"><i />{card.source || 'what Orbit read'}</span>
            <span className="odeck-conf">confidence {conf}% <i><b style={{ width: `${conf}%` }} /></i></span>
          </div>
          <div className="odeck-claim">
            {(card.facts || []).map((f) => {
              const d = decisions[f.field_key];
              const action = d ? d.action : 'confirm';
              return (
                <div key={f.field_key} className="odeck-claim-row">
                  <span>{f.label}</span>
                  <div className="row-actions">
                    <button type="button" className={action === 'confirm' ? 'on' : ''} onClick={() => setRow(f.field_key, f.scope || '', 'confirm')}>Right</button>
                    <button type="button" className={action === 'correct' ? 'on' : ''} onClick={() => setRow(f.field_key, f.scope || '', 'correct')}>Not quite</button>
                    <button type="button" className={action === 'skip' ? 'on' : ''} onClick={() => setRow(f.field_key, f.scope || '', 'skip')}>Leave</button>
                  </div>
                  {action !== 'correct' && <mark className={action === 'skip' ? 'dim' : ''}>{f.value_display}</mark>}
                  {action === 'correct' && (
                    <input
                      value={corrections[f.field_key] ?? f.value_display}
                      onChange={(e) => { const v = e.target.value; setCorrections((c) => ({ ...c, [f.field_key]: v })); setDecisions((prev) => ({ ...prev, [f.field_key]: { field_key: f.field_key, scope: f.scope || '', action: 'correct', value: v } })); }}
                      aria-label={`Correct ${f.label}`}
                    />
                  )}
                  <small>{f.source}</small>
                </div>
              );
            })}
            <small>{(card.facts || []).length} facts read · nothing shared back</small>
          </div>
          <div className="odeck-confirm-row">
            <button type="button" className="yes" onClick={confirmAll} disabled={busy}>That's right</button>
            <button type="button" className="no" onClick={() => onAnswer({ how: 'edited', value: 'later' })}>Later</button>
          </div>
        </>
      );
    }
    if (card.input === 'confirm_org') {
      return (
        <>
          <div className="odeck-org">
            {(card.proposals || []).map((p) => {
              let line = p.title;
              if (p.kind === 'department') line = p.tools.length ? `Department · uses ${p.tools.join(', ')}` : 'Department';
              else if (p.department) line = `${p.title || 'Person'} · ${p.department}`;
              const off = offRows.includes(p.id);
              return (
                <div key={p.id} className={`odeck-org-row ${off ? 'off' : ''}`}>
                  <span className="glyph">{initials(p.name)}</span>
                  <div className="meta"><div>{p.name}</div><small>{line}{p.source ? ` · from ${p.source.replace(/^doc:|^url:/, '')}` : ''}</small></div>
                  <button type="button" onClick={() => setOffRows((rows) => (off ? rows.filter((r) => r !== p.id) : [...rows, p.id]))}>{off ? 'Keep' : 'Remove'}</button>
                </div>
              );
            })}
          </div>
          <div className="odeck-confirm-row">
            <button type="button" className="yes" onClick={orgConfirm} disabled={busy}>That's right</button>
            <button type="button" className="no" onClick={() => onAnswer({ how: 'edited', value: 'later' })}>Later</button>
          </div>
        </>
      );
    }
    if (card.input === 'choose') {
      return <PickChips options={card.options || []} multi={false} picked={picked} onPick={pickOne} />;
    }
    if (card.input === 'choose_many') {
      let otherHint = 'Type it and press Enter.';
      if (card.search === 'tools' && searchTools) otherHint = 'Type a few letters. Orbit searches every tool it can connect to.';
      const detected = (card.options || []).some((o) => o.detected);
      return (
        <>
          {detected && <small className="odeck-detected">{card.detectedNote || 'Orbit already found the ones switched on. Tap to change.'}</small>}
          <PickChips options={card.options || []} multi logo={card.search === 'tools'} picked={picked} onPick={pickMany} />
          {extra.length > 0 && (
            <div className="odeck-chips">
              {extra.map((o) => (
                <button key={o.value} type="button" className="odeck-chip logo picked" onClick={() => setExtra((prev) => prev.filter((x) => x.value !== o.value))} aria-label={`Remove ${o.label}`}>
                  <span className="glyph">{initials(o.label)}</span>{o.label}<span className="odeck-x">×</span>
                </button>
              ))}
            </div>
          )}
          {otherOpen && (
            <div className="odeck-other">
              <div className="odeck-free">
                <input
                  value={otherText}
                  onChange={(e) => setOtherText(e.target.value)}
                  placeholder={card.search === 'tools' ? 'SAP, CargoWise, Xero' : 'Something else'}
                  aria-label="Other"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter' && otherText.trim()) addExtra({ value: otherText.trim(), label: otherText.trim() }); }}
                />
                <button type="button" className="go" onClick={() => addExtra({ value: otherText.trim(), label: otherText.trim() })} disabled={busy || !otherText.trim()}>Add</button>
              </div>
              <small>{searching ? 'Searching' : otherHint}</small>
              {found.length > 0 && (
                <div className="odeck-chips">
                  {found.map((o) => <button key={o.value} type="button" className="odeck-chip logo" onClick={() => addExtra(o)}><span className="glyph">{initials(o.label)}</span>{o.label}</button>)}
                </div>
              )}
            </div>
          )}
          <div><button type="button" className="odeck-chip dashed" onClick={doneChoosing} disabled={busy || chosen.length === 0}>Done choosing →</button></div>
        </>
      );
    }
    if (card.input === 'people') {
      const options = (card.people || []).map((p) => ({ value: p.email, label: `${p.name}${p.department_name ? ` · ${p.department_name}` : ''}`, glyph: initials(p.name) }));
      return (
        <>
          {options.length > 0 && <PickChips options={options} multi picked={picked} onPick={pickMany} />}
          <div className="odeck-free">
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Or type a name and email" aria-label="Name and email" />
            <button type="button" className="go" onClick={submitText} disabled={busy || !text.trim()}>{primaryNow}</button>
          </div>
          <div className="odeck-chips"><button type="button" className="odeck-chip dashed" onClick={() => { if (picked.length) onAnswer({ how: 'multi', value: picked }); }} disabled={busy || picked.length === 0}>Done choosing →</button><button type="button" className="odeck-chip" onClick={() => onAnswer({ how: 'later', value: 'Add people later' })}>I will add people later</button></div>
        </>
      );
    }
    if (card.input === 'evidence') return dropZone;
    if (card.input === 'text' || card.input === 'number') {
      return (
        <div className="odeck-free">
          <input
            type={card.input === 'number' ? 'number' : 'text'}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={card.placeholder || ''}
            aria-label={card.title}
            onKeyDown={(e) => { if (e.key === 'Enter') submitText(); }}
          />
          <button type="button" className="go" onClick={submitText} disabled={busy || !text.trim()}>{primaryNow}</button>
        </div>
      );
    }
    if (card.input === 'password') {
      const long = pw.first.length >= 8;
      const same = pw.first === pw.again;
      let hint = 'Eight characters or more.';
      let hintCls = 'hint';
      if (pw.first && !long) { hint = 'A little longer: eight characters or more.'; hintCls = 'hint warn'; }
      if (long && pw.again && !same) { hint = 'The two do not match yet.'; hintCls = 'hint warn'; }
      if (long && same && pw.again) { hint = 'Good. This is what you sign in with from now on.'; hintCls = 'hint ok'; }
      return (
        <div className="odeck-pw">
          <input value={pw.first} onChange={(e) => setPw({ ...pw, first: e.target.value })} placeholder="New password" type="password" autoComplete="new-password" aria-label="New password" autoFocus />
          <input value={pw.again} onChange={(e) => setPw({ ...pw, again: e.target.value })} placeholder="Type it again" type="password" autoComplete="new-password" aria-label="Repeat the new password" onKeyDown={(e) => { if (e.key === 'Enter' && long && same) onAnswer({ how: 'password', value: pw.first }); }} />
          <span className={hintCls}>{hint}</span>
          <button type="button" className="go" disabled={busy || !long || !same} onClick={() => onAnswer({ how: 'password', value: pw.first })}>Reset password</button>
        </div>
      );
    }
    if (card.input === 'identity') {
      const valid = identity.name.trim().length > 0 && identity.email.includes('@') && identity.password.length >= 8;
      return (
        <>
          {card.recap && card.recap.length > 0 && <div className="odeck-recap">{card.recap.map((r) => <span key={r}>{r}</span>)}</div>}
          <div className="odeck-free col">
            <input value={identity.name} onChange={(e) => setIdentity({ ...identity, name: e.target.value })} placeholder="Your name" autoComplete="name" aria-label="Your name" />
            <input value={identity.email} onChange={(e) => setIdentity({ ...identity, email: e.target.value })} placeholder="Work email" type="email" autoComplete="email" aria-label="Work email" />
            <input value={identity.password} onChange={(e) => setIdentity({ ...identity, password: e.target.value })} placeholder="A password, 8 characters or more" type="password" autoComplete="new-password" aria-label="Password" />
            <button type="button" className="go wide" disabled={busy || !valid} onClick={() => onAnswer({ how: 'given', value: identity.email, identity })}>Send me the first read</button>
          </div>
        </>
      );
    }
    return (
      <>
        {card.options && card.options.length > 0 && <PickChips options={card.options} multi={false} picked={picked} onPick={pickOne} />}
        {voiceUi}
        <div className="odeck-free say">
          <textarea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={card.placeholder || 'Or type it. Plain words are enough.'}
            aria-label={card.title}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.shiftKey) return;
              e.preventDefault();
              submitText();
            }}
          />
          <div className="say-act">
            <small>Enter sends it. Shift and Enter for a new line.</small>
            <button type="button" className="go" onClick={submitText} disabled={busy || !text.trim()}>{primaryNow}</button>
          </div>
        </div>
      </>
    );
  })();

  let foot: React.ReactNode = (
    <div className="odeck-foot">
      <div className="odeck-alt">
        {transcribe && card.input !== 'say' && <button type="button" className="odeck-alt-btn" onClick={() => { if (voice.state === 'idle') startRecording(); }}><Mic /> Say it instead</button>}
        {card.input !== 'evidence' && <button type="button" className="odeck-alt-btn" onClick={() => fileInput.current?.click()}><FileText /> Answer with a document</button>}
      </div>
      <button type="button" className="odeck-skip" onClick={onSkip} disabled={busy}>{skipLabel}</button>
      {card.input !== 'evidence' && <input ref={fileInput} type="file" hidden accept=".pdf,.doc,.docx,.txt,.md,.csv,.png,.jpg,.jpeg" onChange={(e) => takeFile(e.target.files?.[0])} />}
    </div>
  );
  if (card.noSkip) foot = <div className="odeck-foot center"><span>{card.footNote || 'Used to send the first read and protect company ownership. Nothing else.'}</span></div>;
  if (card.input === 'password') foot = <div className="odeck-foot center"><span>Sign in at the login page with your email from now on. No link needed.</span></div>;

  const voiceOnNonSay = transcribe && card.input !== 'say' && voice.state !== 'idle' && voiceUi;

  if (work && work.passes && work.passes.length > 0) {
    return <DeckRead work={work} num={num} onDone={onReadDone} />;
  }

  if (work) {
    return (
      <>
        <div className="odeck-top"><span className="odeck-num">{num}</span><span className="odeck-tag confirm">Orbit is working</span></div>
        <h2>{work.title}</h2>
        {work.note && <p className="odeck-why">{work.note}</p>}
        <ol className="odeck-work" aria-live="polite">
          {work.steps.map((step) => (
            <li key={`${step.key}-${step.text}`} className={step.state}>
              <i aria-hidden />
              <span>{step.text}</span>
            </li>
          ))}
          {work.steps.length === 0 && <li className="doing"><i aria-hidden /><span>Starting</span></li>}
        </ol>
        <div className="odeck-foot center"><span>Every line above is a step Orbit actually took. Nothing is written until you confirm it.</span></div>
      </>
    );
  }

  return (
    <>
      <div className="odeck-top"><span className="odeck-num">{num}</span><span className={`odeck-tag ${card.tag}`}>{card.tagLabel}</span></div>
      <h2>{card.title}</h2>
      {card.why && <p className="odeck-why">{card.why}</p>}
      <div className="odeck-body"><fieldset className="odeck-lock" disabled={busy}>{voiceOnNonSay || body}</fieldset></div>
      {error && <p className="odeck-error" role="alert">{error}</p>}
      {busy && <div className="odeck-busy" role="status"><i /><span>{busyLabel}</span></div>}
      {foot}
    </>
  );
};

export default DeckCard;
